import clsx from 'clsx';
import { Star, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addDays, isoWeekday, weekStart } from '@pz/core';
import type { EntryView, ProcOutput } from '@pz/server';
import { d, h, kw, wd } from '../lib/format';
import { useM, useQ } from '../lib/hooks';
import { Button, Checkbox, ErrorBox, Input, Loading, Modal, Notice, Pill, Select, useToast } from './ui';

/**
 * Quick time entry: favorite tasks (star), several days at once and copying a week or a template. Everything here only
 * prepares bookings; each row is booked by the normal route (time.createMany), so caps, closed months, the step and the
 * mandatory description apply exactly as for a single entry.
 */

export type PlanRowView = ProcOutput<'quick.copyPlan'>['rows'][number];

/** Days booked at once (the server takes at most 60 rows per call). */
export const MAX_DAYS = 60;

/* ------------------------------------------------------------------ */
/* Favorites                                                            */
/* ------------------------------------------------------------------ */

/** The pinned tasks of the person (stored on the server, so they follow to every device). */
export function useFavorites(enabled = true) {
  const toast = useToast();
  const q = useQ('quick.favorites', {}, { enabled });
  const toggle = useM('quick.toggleFavorite', { invalidate: ['quick.'] });
  const tasks = useMemo(() => q.data?.tasks ?? [], [q.data]);
  const ids = useMemo(() => new Set(tasks.map((x) => x.id)), [tasks]);
  // own hours per task of the last 90 days (side panel "most hours first")
  const usage = useMemo(() => q.data?.usage ?? {}, [q.data]);
  return {
    enabled,
    tasks,
    ids,
    usage,
    loading: q.isLoading,
    pending: toggle.isPending,
    toggle: (taskId: string) => toggle.mutateAsync({ taskId }).catch(toast.error),
  };
}

export function FavoriteStar({ on, onToggle, disabled, className }: { on: boolean; onToggle: () => void; disabled?: boolean; className?: string }) {
  const { t } = useTranslation();
  const label = on ? t('quick.unpin') : t('quick.pin');
  return (
    <button
      type="button"
      aria-pressed={on}
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggle();
      }}
      className={clsx('shrink-0 rounded p-1.5 hover:bg-sheet-2 disabled:opacity-50', on ? 'text-warn' : 'text-faint hover:text-graphite', className)}
    >
      <Star className={clsx('size-4', on && 'fill-current')} />
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Review of planned bookings                                           */
/* ------------------------------------------------------------------ */

/** A row is ticked from the start when it can be booked and there is nothing to look at twice. */
const tickedAtStart = (r: PlanRowView) => !r.problem && !r.hint;

function usePlanText() {
  const { t } = useTranslation();
  return {
    problem: (code: string) => t(`errors.bookability.${code}` as 'errors.bookability.NOT_MEMBER', { defaultValue: t(`errors.${code}` as 'errors.INTERNAL', { defaultValue: code }) }),
    hint: (code: string) => t(`quick.hint.${code}` as 'quick.hint.HOLIDAY', { defaultValue: code }),
  };
}

/**
 * State of a list of planned bookings between "prepared" and "booked": which rows are ticked, which were booked, which
 * failed and why. A row that failed stays in the list with the reason; booked rows leave it, so booking again never
 * duplicates.
 */
export function useReview(planned: PlanRowView[] | undefined) {
  const m = useM('time.createMany', { invalidate: ['time.', 'tasks.recent', 'kpi.', 'approvals.', 'cockpit.'] });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [done, setDone] = useState<Set<string>>(new Set());
  const [failed, setFailed] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    setSelected(new Set((planned ?? []).filter(tickedAtStart).map((r) => r.key)));
    setDone(new Set());
    setFailed(new Map());
  }, [planned]);
  const rows = useMemo(() => (planned ?? []).filter((r) => !done.has(r.key)), [planned, done]);
  const todo = rows.filter((r) => selected.has(r.key) && !r.problem);
  const book = async () => {
    const res = await m.mutateAsync({
      rows: todo.map((r) => ({ taskId: r.taskId, workDate: r.date, minutes: r.minutes, description: r.description, billingType: r.billingType, workLocation: r.workLocation })),
    });
    const nextDone = new Set(done);
    const nextFailed = new Map<string, string>();
    res.results.forEach((x, i) => (x.ok ? nextDone.add(todo[i].key) : nextFailed.set(todo[i].key, x.code)));
    setDone(nextDone);
    setFailed(nextFailed);
    return { created: res.created, failed: res.failed };
  };
  return {
    rows,
    selected,
    setSelected,
    failed,
    count: todo.length,
    minutes: todo.reduce((s, r) => s + r.minutes, 0),
    pending: m.isPending,
    error: m.error,
    book,
  };
}
export type Review = ReturnType<typeof useReview>;

/** The prepared bookings with a tick box each: day, task, hours and, where there is one, the note or the reason it fails. */
export function PlanReview({ review }: { review: Review }) {
  const { t } = useTranslation();
  const text = usePlanText();
  const { rows, selected, setSelected, failed } = review;
  const selectable = rows.filter((r) => !r.problem);
  const all = selectable.length > 0 && selectable.every((r) => selected.has(r.key));
  const some = selectable.some((r) => selected.has(r.key));
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Checkbox
          label={t('common.selectAll')}
          checked={all}
          indeterminate={some && !all}
          disabled={selectable.length === 0}
          onChange={() => setSelected(all ? new Set() : new Set(selectable.map((r) => r.key)))}
        />
        <span className="num text-xs text-graphite">{t('quick.selected', { count: review.count, total: rows.length, hours: h(review.minutes) })}</span>
      </div>
      <ul className="max-h-80 overflow-y-auto rounded-md border border-line">
        {rows.map((r) => {
          const blocked = !!r.problem;
          const err = failed.get(r.key);
          return (
            <li key={r.key} className={clsx('flex items-start gap-3 border-b border-line/60 px-3 py-2 last:border-b-0', blocked ? 'bg-sheet-2 text-graphite' : !selected.has(r.key) && 'text-graphite')}>
              <input
                type="checkbox"
                className="mt-1 size-4 accent-[var(--accent)]"
                aria-label={`${wd(r.date)} ${d(r.date)} ${r.taskRef} ${r.taskTitle}`}
                disabled={blocked}
                checked={!blocked && selected.has(r.key)}
                onChange={(e) => {
                  const next = new Set(selected);
                  if (e.target.checked) next.add(r.key);
                  else next.delete(r.key);
                  setSelected(next);
                }}
              />
              <div className="w-20 shrink-0 text-sm whitespace-nowrap">
                <span className="font-medium">{wd(r.date)}</span> <span className="num text-graphite">{d(r.date).slice(0, 6)}</span>
              </div>
              <div className="min-w-0 flex-1 text-[13px] leading-snug">
                {r.taskRef ? (
                  <>
                    <span className="num font-semibold text-accent">{r.taskRef}</span> <span className="font-medium">{r.taskTitle}</span>
                    <span className="block truncate text-xs text-graphite">
                      {r.projectNumber} {r.projectName}
                      {r.description ? ` · ${r.description}` : ''}
                    </span>
                  </>
                ) : (
                  <span className="text-graphite">{t('quick.unknownTask')}</span>
                )}
                {(r.problem || r.hint || err) && (
                  <span className="mt-1 flex flex-wrap gap-1">
                    {r.problem && <Pill tone="bad">{text.problem(r.problem)}</Pill>}
                    {!r.problem && r.hint && <Pill tone="warn">{text.hint(r.hint)}</Pill>}
                    {err && <Pill tone="bad">{text.problem(err)}</Pill>}
                  </span>
                )}
              </div>
              <span className="num text-sm font-semibold">{h(r.minutes)}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Copy a week or a template                                            */
/* ------------------------------------------------------------------ */

type Template = { id: string; name: string; rows: { taskId: string; weekday: number; minutes: number; description?: string; billingType?: 'billable' | 'included' | 'non_billable'; workLocation?: string | null }[] };
const MAX_TEMPLATES = 20;

/** Template rows from the entries of the shown week (the description is cut to the length a template keeps). */
function templateRows(entries: EntryView[]): Template['rows'] {
  return entries
    .filter((e) => !e.deletedAt)
    .slice(0, MAX_DAYS)
    .map((e) => ({
      taskId: e.taskId,
      weekday: isoWeekday(e.workDate),
      minutes: e.minutes,
      description: e.description.slice(0, 500) || undefined,
      billingType: e.billingType,
      workLocation: e.workLocation || null,
    }));
}

/**
 * Takes the entries of last week, of any other week or of a saved template over into the shown week, as drafts, after
 * a look at every line. The shown week can be saved as a template here as well.
 */
export function CopyWeekDialog({ open, onClose, targetWeek, shownEntries }: { open: boolean; onClose: () => void; targetWeek: string; shownEntries: EntryView[] }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [source, setSource] = useState('last');
  const [otherDate, setOtherDate] = useState('');
  const [name, setName] = useState('');
  const prefs = useQ('prefs.get', { keys: ['quick.templates'] }, { enabled: open });
  const templates = useMemo(() => (prefs.data?.['quick.templates'] ?? []) as Template[], [prefs.data]);
  const setPrefs = useM('prefs.set', { invalidate: ['prefs.'] });
  useEffect(() => {
    if (open) {
      setSource('last');
      setOtherDate('');
      setName('');
    }
  }, [open, targetWeek]);
  const src =
    source === 'last'
      ? ({ kind: 'week', weekStart: addDays(targetWeek, -7) } as const)
      : source === 'other'
        ? otherDate
          ? ({ kind: 'week', weekStart: weekStart(otherDate) } as const)
          : null
        : ({ kind: 'template', id: source.slice(4) } as const);
  const plan = useQ('quick.copyPlan', { source: src ?? { kind: 'week', weekStart: addDays(targetWeek, -7) }, targetWeek }, { enabled: open && !!src, gcTime: 0, staleTime: 0, placeholderData: undefined });
  const review = useReview(src ? plan.data?.rows : undefined);
  const current = templates.find((x) => `tpl:${x.id}` === source);
  const ownRows = templateRows(shownEntries);

  const saveTemplate = async () => {
    const tpl: Template = { id: Math.random().toString(36).slice(2, 10), name: name.trim(), rows: ownRows };
    await setPrefs.mutateAsync({ key: 'quick.templates', value: [...templates, tpl] });
    toast.ok(t('quick.templateSaved', { name: tpl.name }));
    setName('');
    setSource(`tpl:${tpl.id}`);
  };
  const deleteTemplate = async (tpl: Template) => {
    const before = templates;
    await setPrefs.mutateAsync({ key: 'quick.templates', value: before.filter((x) => x.id !== tpl.id) });
    setSource('last');
    toast.undoable(t('quick.templateDeleted'), t('common.undo'), () => setPrefs.mutateAsync({ key: 'quick.templates', value: before }).catch(toast.error));
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      wide
      title={t('quick.copyTitle')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            disabled={review.count === 0}
            loading={review.pending}
            onClick={() =>
              review
                .book()
                .then((r) => {
                  if (r.failed === 0) {
                    toast.ok(t('quick.doneMany', { count: r.created }));
                    onClose();
                  } else toast.warn(t('quick.partial', { ok: r.created, failed: r.failed }));
                })
                .catch(() => {})
            }
          >
            {t('quick.bookNDraft', { count: review.count })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-graphite">{t('quick.copyIntro', { week: kw(targetWeek) })}</p>
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex min-w-60 flex-1 flex-col gap-1 text-sm font-medium">
            {t('quick.source')}
            <Select value={source} onChange={(e) => setSource(e.target.value)}>
              <option value="last">{t('quick.sourceLast', { week: kw(addDays(targetWeek, -7)) })}</option>
              <option value="other">{t('quick.sourceOther')}</option>
              {templates.map((x) => (
                <option key={x.id} value={`tpl:${x.id}`}>
                  {t('quick.sourceTemplate', { name: x.name })}
                </option>
              ))}
            </Select>
          </label>
          {source === 'other' && (
            <label className="flex flex-col gap-1 text-sm font-medium">
              {t('quick.otherWeek')}
              <Input type="date" className="w-40" value={otherDate} onChange={(e) => setOtherDate(e.target.value)} />
            </label>
          )}
          {current && (
            <Button icon={<Trash2 className="size-4" />} loading={setPrefs.isPending} onClick={() => deleteTemplate(current).catch(() => {})}>
              {t('quick.templateDelete')}
            </Button>
          )}
        </div>

        {src && plan.isLoading && <Loading />}
        <ErrorBox error={plan.error ?? setPrefs.error} />
        {src && plan.data && plan.data.rows.length === 0 && <Notice tone="neutral">{t('quick.emptySource')}</Notice>}
        {src && plan.data && plan.data.rows.length > 0 && (
          <>
            <p className="text-xs text-graphite">{t('quick.reviewIntro')}</p>
            <PlanReview review={review} />
          </>
        )}
        <ErrorBox error={review.error} />

        <details className="rounded-md border border-line bg-sheet-2 px-3 py-2">
          <summary className="cursor-pointer text-sm font-medium">{t('quick.saveTemplate')}</summary>
          <div className="mt-2 flex flex-col gap-2">
            <p className="text-xs text-graphite">{t('quick.templateHint', { week: kw(targetWeek) })}</p>
            {ownRows.length === 0 ? (
              <p className="text-xs text-graphite">{t('quick.templateEmptyWeek', { week: kw(targetWeek) })}</p>
            ) : templates.length >= MAX_TEMPLATES ? (
              <p className="text-xs text-bad">{t('quick.templateLimit')}</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                <Input className="min-w-52 flex-1" aria-label={t('quick.templateName')} placeholder={t('quick.templateNamePlaceholder')} maxLength={60} value={name} onChange={(e) => setName(e.target.value)} />
                <Button disabled={!name.trim()} loading={setPrefs.isPending} onClick={() => saveTemplate().catch(() => {})}>
                  {t('quick.templateSave')}
                </Button>
              </div>
            )}
          </div>
        </details>
      </div>
    </Modal>
  );
}
